import { useState } from 'react';

import { Skeleton, Stack } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { usePaginatedDocuments } from '@/apps/engagement-letter-database/hooks/usePaginatedDocuments';
import { useClientRecord } from '@/apps/engagement-letter-database/store';
import { useBreadcrumb } from '@/shared/components/Breadcrumbs';
import { useCurrentUserMpId } from '@/shared/hooks';

import ClientInfoPane from './components/ClientInfoPane';
import CommentModal from './components/CommentModal';
import CommentsPane from './components/CommentsPane';
import HeaderPane from './components/HeaderPane';
import UserModal from './components/Modal';
import SearchResults from './components/SearchResults';
import SysInfoPane from './components/SysInfoPane';

const DEBOUNCE_MS = 300;

const ClientRecordsDashboard = () => {
  const currentUserMpId = useCurrentUserMpId();

  const { record: clientRecord, isLoading } = useClientRecord();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debouncedSetSearch = useDebouncedCallback((value: string) => setDebouncedSearchTerm(value), DEBOUNCE_MS);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearch(value);
  };
  const {
    rows: documentRows,
    rowCount,
    paginationModel,
    setPaginationModel,
    loading: docsLoading,
  } = usePaginatedDocuments(clientRecord?.clientNumber, debouncedSearchTerm);
  // Set custom breadcrumb to show client name instead of client number
  useBreadcrumb({
    label: clientRecord?.clientName ?? '',
    hideIntermediateSegments: true,
  });

  if (isLoading || !clientRecord) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={300} />
      </Stack>
    );
  }

  function mapClientRecord(record: typeof clientRecord) {
    const clientInfoRecord = {
      clientNumber: record.clientNumber,
      clientName: record.clientName ?? '',
      responsibleAttorney: record.responsibleAttorney?.displayName ?? 'N/A',
      practice: record.practiceGroup ?? 'N/A',
      openDate: record.clientOpenDate?.format('YYYY-MM-DD') ?? '',
      closeDate: record.clientClosedDate?.format('YYYY-MM-DD') ?? '',
    };
    const headerRecord = {
      id: record.id,
      clientName: record.clientName ?? '',
      clientNumber: record.clientNumber,
      letterType: 'engagement' as const,
      closeDate: record.clientClosedDate?.format('YYYY-MM-DD'),
      isExecuted: record.isExecuted,
    };
    return { clientInfoRecord, headerRecord };
  }
  const { clientInfoRecord, headerRecord } = mapClientRecord(clientRecord);

  // Determine the most recent modification across the record, its documents and
  // its comments, and surface that as the "Last Modified" audit info.
  const modificationCandidates = [
    {
      date: clientRecord.updatedAt,
      by: (clientRecord.updatedByUser?.displayName ?? clientRecord.updatedBy) || 'N/A',
    },
    ...clientRecord.iManageDocuments.map((doc) => ({
      date: doc.updatedAt ?? null,
      by: doc.updatedByUser?.displayName ?? 'N/A',
    })),
    ...clientRecord.iManageComments.map((comment) => ({
      date: comment.updatedAt ?? null,
      by: comment.updatedByUser?.displayName ?? 'N/A',
    })),
  ].filter((candidate): candidate is { date: NonNullable<(typeof candidate)['date']>; by: string } =>
    Boolean(candidate.date),
  );

  const latestModification = modificationCandidates.reduce<(typeof modificationCandidates)[number] | null>(
    (latest, candidate) => (!latest || candidate.date.isAfter(latest.date) ? candidate : latest),
    null,
  );

  const sysInfoRecord = {
    createdBy: (clientRecord.createdByUser?.displayName ?? clientRecord.createdBy) || 'N/A',
    createdDate: clientRecord.createdAt?.toISOString() ?? 'N/A',
    modifiedBy: latestModification?.by ?? 'N/A',
    modifiedDate: latestModification?.date.toISOString() ?? 'N/A',
  };

  const comments = clientRecord.iManageComments.map((comment) => ({
    id: comment.id,
    text: comment.comments,
    avatarUrl: comment.updatedByUser?.imageUrlSmall,
    createdDate: comment.updatedAt?.toISOString() ?? 'N/A',
    updatedDate: comment.updatedAt?.toISOString() ?? 'N/A',
    updatedBy: comment.updatedByUser?.displayName ?? 'N/A',
  }));

  return (
    <>
      <HeaderPane record={headerRecord} />
      <ClientInfoPane record={clientInfoRecord} />
      <SearchResults
        rows={documentRows}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        loading={docsLoading}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        recordInfo={clientRecord}
      />
      <CommentsPane record={{ comments }} />
      <SysInfoPane record={sysInfoRecord} />
      <UserModal engagementRecordId={clientRecord.engagementRecordId ?? ''} currentUserMpId={currentUserMpId} />
      <CommentModal engagementRecordId={clientRecord.engagementRecordId ?? ''} currentUserMpId={currentUserMpId} />
    </>
  );
};

export default ClientRecordsDashboard;
